import { useCallback } from 'react';
import {
  baseMediaUrl,
  baseUrl,
  fixEncodingStrings,
  getRequest,
  patchRequest,
  postFormDataRequest,
  postRequest,
  removeAfterSecondAmpersand,
  sendMessageRequest,
  sendMessageUrl,
  sendTemplateMessageUrl,
  webhookTeleinUrl,
} from '../utils/services';
import { useErrorStore } from '../stores/useErrorStore';
import { useAuthStore } from '../stores/useAuthStore';
import { useMessageStore } from '../stores/useMessageStore';
import { useQuickAnswersStore } from '../stores/useQuickAnswersStore';
import { useSocketStore } from '../stores/useSocketStore';
import { useTicketStore } from '../stores/useTicketStore';
import { useUIStore } from '../stores/useUIStore';
import { useUploadStore } from '../stores/useUploadStore';
import {
  updateOptimisticMessageDeliveryStatus,
  updateUserChatsWithNewMessage,
} from '../utils/chatState';
import {
  isMessageItem,
  mergeMessageItems,
  normalizeMessagePageResponse,
} from '../utils/messageVisibility';

export function useChatActions({
  handleScrollChatToBottom = () => {},
  updateUserChatFromQueueToChats = async () => {},
} = {}) {
  const user = useAuthStore((state) => state.user);

  const socket = useSocketStore((state) => state.socket);

  const currentChat = useTicketStore((state) => state.currentChat);
  const messages = useMessageStore((state) => state.messages);
  const messageOffset = useMessageStore((state) => state.messageOffset);
  const isAComment = useMessageStore((state) => state.isAComment);
  const officialApiTemplates = useMessageStore((state) => state.officialApiTemplates);
  const openTickets = useTicketStore((state) => state.openTickets);
  const sortedChats = useTicketStore((state) => state.sortedChats);
  const setCurrentChat = useTicketStore((state) => state.setCurrentChat);
  const removeChatFromCollections = useTicketStore((state) => state.removeChatFromCollections);

  const setMessages = useMessageStore((state) => state.setMessages);
  const setQuotedMessages = useMessageStore((state) => state.setQuotedMessages);
  const setMessagesPage = useMessageStore((state) => state.setMessagesPage);
  const setMessageOffset = useMessageStore((state) => state.setMessageOffset);
  const setMessageAccess = useMessageStore((state) => state.setMessageAccess);
  const setHasMoreMessages = useMessageStore((state) => state.setHasMoreMessages);
  const setIsMessagesLoading = useMessageStore((state) => state.setIsMessagesLoading);
  const setMessagesError = useMessageStore((state) => state.setMessagesError);
  const setIsMessageSendingError = useMessageStore((state) => state.setIsMessageSendingError);
  const setIsStartingNewChatTemplate = useMessageStore(
    (state) => state.setIsStartingNewChatTemplate,
  );
  const setSearchMessageValue = useMessageStore((state) => state.setSearchMessageValue);
  const setOfficialApiTemplates = useMessageStore((state) => state.setOfficialApiTemplates);
  const setAudioTranscription = useMessageStore((state) => state.setAudioTranscription);

  const setOpenTickets = useTicketStore((state) => state.setOpenTickets);
  const setSortedChats = useTicketStore((state) => state.setSortedChats);

  const setShowNewChat = useUIStore((state) => state.setShowNewChat);
  const setIsEditingProfileChat = useUIStore((state) => state.setIsEditingProfileChat);
  const setIsEditingProfileGroupChat = useUIStore((state) => state.setIsEditingProfileGroupChat);
  const showAlert = useUIStore((state) => state.showAlert);

  const urlImageToSend = useUploadStore((state) => state.urlImageToSend);
  const urlVideoToSend = useUploadStore((state) => state.urlVideoToSend);
  const urlDocumentToSend = useUploadStore((state) => state.urlDocumentToSend);
  const audioBlobURL = useUploadStore((state) => state.audioBlobURL);
  const setQuickAnswers = useQuickAnswersStore((state) => state.setQuickAnswers);

  const setErrorMessage = useErrorStore((state) => state.setErrorMessage);

  const buildChatRequestKey = useCallback((chat) => {
    if (!chat) {
      return null;
    }

    return chat.is_internal_chat === 1
      ? `internal:${chat.id}`
      : `external:${chat.sender}:${chat.fromMsg}`;
  }, []);

  const isRequestStillActive = useCallback((requestKey) => {
    if (!requestKey) {
      return true;
    }

    return buildChatRequestKey(useTicketStore.getState().currentChat) === requestKey;
  }, [buildChatRequestKey]);

  const resetChatMessagesState = useCallback(() => {
    setMessages([]);
    setQuotedMessages([]);
    setMessagesPage(1);
    setMessageOffset(0);
    setMessageAccess();
    setHasMoreMessages(true);
    setMessagesError(null);
    setSearchMessageValue('');
    setQuickAnswers((prevQuickAnswers) => ({
      ...prevQuickAnswers,
      messagesWithMediaUrl: [],
    }));
    setIsEditingProfileChat(false);
    setIsEditingProfileGroupChat(false);
    setOfficialApiTemplates((prevTemplates) => ({
      ...prevTemplates,
      isOfficialApiMessageExpired: false,
      isOfficialApiTemplateSended: false,
    }));
  }, [
    setHasMoreMessages,
    setIsEditingProfileChat,
    setIsEditingProfileGroupChat,
    setMessages,
    setMessagesError,
    setMessagesPage,
    setMessageAccess,
    setMessageOffset,
    setOfficialApiTemplates,
    setQuotedMessages,
    setQuickAnswers,
    setSearchMessageValue,
  ]);

  const fetchMediaUrls = useCallback(async (response) => {
    if (Array.isArray(response)) {
      const messagesWithMedia = await Promise.all(
        response.map(async (res) => {
          if (!isMessageItem(res)) return res;

          if (
            !res.mediaType
            || res.mediaType === 'text/notification'
            || res.mediaType === 'quotedMessage'
            || res.mediaType === 'contactMessage'
            || res.mediaType === 'reactMsg'
            || res.mediaType === 'template'
            || res.api === 2
          ) return res;

          try {
            if (res.mediaType === 'video/mp4') {
              const thumbResponse = await getRequest(
                `${baseMediaUrl}${res.mediaUrl}.thumb`,
              );
              res.videoThumb = thumbResponse.url;
            }

            const mediaResponse = await getRequest(
              `${baseMediaUrl}${res.mediaUrl}`,
            );
            res.mediaUrl = mediaResponse.url;
          } catch (error) {
            setErrorMessage('Erro ao buscar mídia da mensagem');
          }

          return res;
        }),
      );

      return messagesWithMedia;
    }

    if (response && typeof response === 'object' && isMessageItem(response)) {
      if (response.mediaType && response.mediaType !== '') {
        try {
          if (response.mediaType === 'video/mp4') {
            const thumbResponse = await getRequest(
              `${baseMediaUrl}${response.mediaUrl}.thumb`,
            );
            response.videoThumb = thumbResponse.url;
          }

          const mediaResponse = await getRequest(
            `${baseMediaUrl}${response.mediaUrl}`,
          );
          response.mediaUrl = mediaResponse.url;
        } catch (error) {
          setErrorMessage('Erro ao buscar mídia da mensagem');
        }
      }

      return response;
    }

    return setErrorMessage(
      'Erro ao receber mensagem com mídia. Entre em contato com o suporte.',
    );
  }, [setErrorMessage]);

  const getMessages = useCallback(async (sender, fromMsg, offset, limit, requestKey) => {
    if (!sender || !fromMsg) {
      return normalizeMessagePageResponse([]);
    }

    setMessagesError(null);

    try {
      const response = await getRequest(
        `${baseUrl}/messages/${sender}/${fromMsg}/?limit=${limit}&offset=${offset}`,
      );

      if (response.error && response.code === 'RESOURCE_OUT_OF_SCOPE') {
        if (isRequestStillActive(requestKey)) {
          removeChatFromCollections({ sender, fromMsg });
          setCurrentChat(null);
          resetChatMessagesState();
          showAlert(
            response.message || 'Este atendimento não está disponível para o seu perfil de acesso.',
            'warning',
            5000,
          );
        }
        return normalizeMessagePageResponse([]);
      }

      if (response.error && response.type === 'NOT_FOUND') {
        if (isRequestStillActive(requestKey)) {
          setHasMoreMessages(false);
        }
        return normalizeMessagePageResponse([]);
      }

      if (!isRequestStillActive(requestKey)) {
        return normalizeMessagePageResponse([]);
      }

      const messagePage = normalizeMessagePageResponse(response);
      const originalMessages = JSON.parse(JSON.stringify(messagePage.items));

      setMessageAccess(messagePage.access);
      setMessageOffset(messagePage.page.nextOffset);

      setQuickAnswers((prevQuickAnswers) => ({
        ...prevQuickAnswers,
        messagesWithMediaUrl: [
          ...prevQuickAnswers.messagesWithMediaUrl,
          ...originalMessages.filter(isMessageItem),
        ],
      }));

      const quotedMessages = [];

      const quotedMessagePromises = originalMessages
        .filter(isMessageItem)
        .map(async (msg) => {
        if (msg.mediaType === 'quotedMessage' && msg.quotedMsgId) {
          const quotedMessageResponse = await getRequest(
            `${baseUrl}/messages/${msg.quotedMsgId}`,
          );

          if (
            quotedMessageResponse.error
            && quotedMessageResponse.code === 'RESOURCE_OUT_OF_SCOPE'
          ) {
            return;
          }

          if (
            quotedMessageResponse.error
            && quotedMessageResponse.statusCode === 404
          ) {
            const quotedMessageWithError = {
              ...quotedMessageResponse,
              idMsg: msg.quotedMsgId,
            };

            if (isRequestStillActive(requestKey)) {
              setQuotedMessages((prevQuotedMessages) => [
                ...prevQuotedMessages,
                quotedMessageWithError,
              ]);
            }
            return;
          }

          quotedMessages.push(quotedMessageResponse);
        }
      });

      await Promise.all(quotedMessagePromises);

      if (!isRequestStillActive(requestKey)) {
        return [];
      }

      const quotedMessagesWithMedia = await fetchMediaUrls(quotedMessages);

      if (isRequestStillActive(requestKey)) {
        setQuotedMessages((prevQuotedMessages) => [
          ...prevQuotedMessages,
          ...quotedMessagesWithMedia,
        ]);
      }

      const messagesWithMedia = await fetchMediaUrls(originalMessages);

      const decodedMessages = messagesWithMedia.map((msg) => {
        const updatedMsg = { ...msg };

        if (msg.body) {
          updatedMsg.body = fixEncodingStrings(msg.body);
        }

        if (msg.reacted) {
          updatedMsg.reacted = removeAfterSecondAmpersand(msg.reacted);
        }

        return updatedMsg;
      });

      const unreadMessages = decodedMessages.filter(
        (message) => isMessageItem(message) && !message.read && message.fromMe !== 1,
      );

      await Promise.all(unreadMessages.map(
        async (message) => patchRequest(`${baseUrl}/messages/updateToRead/${message.id}`),
      ));

      if (isRequestStillActive(requestKey)) {
        setHasMoreMessages(messagePage.page.hasMore);
      }

      return {
        ...messagePage,
        items: decodedMessages,
      };
    } catch (error) {
      if (isRequestStillActive(requestKey)) {
        setErrorMessage(
          'Erro ao carregar mensagens! Entre em contato com o suporte.',
        );
      }
      return normalizeMessagePageResponse([]);
    } finally {
      if (isRequestStillActive(requestKey)) {
        setIsMessagesLoading(false);
      }
    }
  }, [
    fetchMediaUrls,
    isRequestStillActive,
    resetChatMessagesState,
    setErrorMessage,
    setCurrentChat,
    setHasMoreMessages,
    setIsMessagesLoading,
    setMessageAccess,
    setMessageOffset,
    setMessagesError,
    setQuickAnswers,
    setQuotedMessages,
    removeChatFromCollections,
    showAlert,
  ]);

  const fetchInternalMessages = useCallback(async (selectedChat, messagesPage) => {
    setIsEditingProfileGroupChat(false);

    try {
      const MESSAGE_PER_PAGE = 50;
      if (!selectedChat) return;

      const requestKey = buildChatRequestKey(selectedChat);
      const isFirstPage = messagesPage === 1;

      if (isFirstPage) {
        resetChatMessagesState();
      }

      setIsMessagesLoading(true);

      const response = await getRequest(
        `${baseUrl}/messages/internalMessages/${selectedChat.id}?page=${messagesPage}&limit=${MESSAGE_PER_PAGE}`,
      );

      if (!isRequestStillActive(requestKey)) {
        return;
      }

      if (response.length > 0) {
        const firstRenderedMessageId = isFirstPage
          ? response[0]?.id
          : useMessageStore.getState().messages[0]?.id || response[0]?.id;

        await patchRequest(
          `${baseUrl}/messages/updateInternalMessagesToRead/${user.id}/${selectedChat.id}/${firstRenderedMessageId}`,
        );
      }

      await Promise.all(response.map(async (msg) => {
        if (!msg.quotedMsgId) {
          return;
        }

        const quotedMessageResponse = await getRequest(
          `${baseUrl}/messages/internalMessage/${msg.quotedMsgId}`,
        );

        if (isRequestStillActive(requestKey)) {
          setQuotedMessages((prevQuotedMessages) => [
            ...prevQuotedMessages,
            quotedMessageResponse,
          ]);
        }
      }));

      if (!isRequestStillActive(requestKey)) {
        return;
      }

      setMessages((prevMessages) => {
        if (!isRequestStillActive(requestKey)) {
          return prevMessages;
        }

        const uniqueMessages = response.filter(
          (newMsg) => !prevMessages.some((prevMsg) => prevMsg.id === newMsg.id),
        );

        return isFirstPage
          ? [...response]
          : [...prevMessages, ...uniqueMessages];
      });

      setHasMoreMessages(response.length === MESSAGE_PER_PAGE);
      setOfficialApiTemplates((prevTemplates) => ({
        ...prevTemplates,
        isOfficialApiMessageExpired: false,
      }));
      if (isFirstPage) {
        setTimeout(handleScrollChatToBottom, 500);
      }
    } catch (error) {
      if (isRequestStillActive(buildChatRequestKey(selectedChat))) {
        setErrorMessage(
          'Não foi possível carregar mensagens! Entre em contato com o suporte.',
        );
      }
    } finally {
      if (isRequestStillActive(buildChatRequestKey(selectedChat))) {
        setIsMessagesLoading(false);
      }
    }
  }, [
    buildChatRequestKey,
    handleScrollChatToBottom,
    isRequestStillActive,
    resetChatMessagesState,
    setErrorMessage,
    setHasMoreMessages,
    setIsEditingProfileGroupChat,
    setIsMessagesLoading,
    setMessages,
    setOfficialApiTemplates,
    setQuotedMessages,
    user?.id,
  ]);

  const fetchMessages = useCallback(async (selectedChat, messagesPage) => {
    if (!selectedChat) return;

    const requestKey = buildChatRequestKey(selectedChat);
    const isFirstPage = messagesPage === 1;

    if (isFirstPage) {
      resetChatMessagesState();
    }

    setIsMessagesLoading(true);

    const fetchedMessagePage = await getMessages(
      selectedChat?.sender,
      selectedChat?.fromMsg,
      isFirstPage ? 0 : messageOffset,
      100,
      requestKey,
    );

    if (!isRequestStillActive(requestKey)) {
      return;
    }

    setMessages((prevMessages) => {
      if (!isRequestStillActive(requestKey)) {
        return prevMessages;
      }

      return isFirstPage
        ? [...fetchedMessagePage.items]
        : mergeMessageItems(prevMessages, fetchedMessagePage.items);
    });

    if (isFirstPage) {
      setTimeout(handleScrollChatToBottom, 500);
    }
  }, [
    buildChatRequestKey,
    getMessages,
    handleScrollChatToBottom,
    isRequestStillActive,
    messageOffset,
    resetChatMessagesState,
    setIsMessagesLoading,
    setMessages,
  ]);

  const appendMessageIfMissing = useCallback((message) => {
    setMessages((prevMessages) => {
      const alreadyExists = prevMessages.some((prevMessage) => (
        prevMessage.clientMessageId === message.clientMessageId
      ));

      if (alreadyExists) {
        return prevMessages;
      }

      return [message, ...prevMessages];
    });
  }, [setMessages]);

  const sendMessage = useCallback(async (
    textMessage,
    sender,
    phoneNumber,
    setTextMessage,
    mediaUrl,
    chatOverride = null,
  ) => {
    setIsMessagesLoading(true);

    if (textMessage === '' && !mediaUrl) {
      setIsMessagesLoading(false);
      return undefined;
    }

    const getMediaType = ({
      currentAudioBlobURL,
      currentUrlImageToSend,
      currentUrlVideoToSend,
      currentUrlDocumentToSend,
    }) => {
      if (currentAudioBlobURL) {
        return 'audio/ogg; codecs=opus';
      }

      if (currentUrlImageToSend.length > 0) {
        return 'image/jpeg';
      }

      if (currentUrlVideoToSend.length > 0) {
        return 'video/mp4';
      }

      if (currentUrlDocumentToSend.length > 0) {
        const isAudioFile = currentUrlDocumentToSend.some(
          (file) => file.endsWith('.mp3')
            || file.endsWith('.ogg')
            || file.endsWith('.mpeg')
            || file.endsWith('.wav')
            || file.endsWith('.WAV'),
        );
        return isAudioFile ? 'audio/ogg; codecs=opus' : 'application/pdf';
      }

      return '';
    };

    const clientMessageId = [
      'outbound',
      sender,
      phoneNumber,
      Date.now(),
      Math.random().toString(36).slice(2),
    ].join('-');
    const activeChat = chatOverride || currentChat;

    const message = {
      body:
        user.showName === 1 ? `*${user.name}*:\n ${textMessage}` : textMessage,
      clientMessageId,
      fromMe: 1,
      idMsg: null,
      isDeleted: null,
      sender,
      ack: 0,
      fromMsg: phoneNumber,
      bodyfile: activeChat?.fromMsg?.endsWith('@g.us') ? '||' : null,
      createdAt: Date.now(),
      mediaType: getMediaType({
        currentAudioBlobURL: audioBlobURL,
        currentUrlImageToSend: urlImageToSend,
        currentUrlVideoToSend: urlVideoToSend,
        currentUrlDocumentToSend: urlDocumentToSend,
      }),
      mediaUrl,
    };

    let responseMediaUrl = {};

    const token = user.senders[sender].sessionId;

    try {
      setTextMessage('');

      setOpenTickets((prevOpenTickets) => updateUserChatsWithNewMessage(
        prevOpenTickets,
        message,
      ));

      setSortedChats((prevSortedChats) => updateUserChatsWithNewMessage(
        prevSortedChats,
        message,
      ));
      setShowNewChat(false);

      const textMessageWithName = user.showName === 1
        ? `*${user.name}*:\n ${textMessage}`
        : textMessage;

      socket.emit(
        'sendedMessage',
        activeChat,
        message,
        user.ambiente,
        user.id,
      );

      setMessages((prevMessages) => [message, ...prevMessages]);
      setTimeout(handleScrollChatToBottom, 500);

      setQuickAnswers((prevQuickAnswers) => ({
        ...prevQuickAnswers,
        messagesWithMediaUrl: [message, ...prevQuickAnswers.messagesWithMediaUrl],
      }));

      const response = await sendMessageRequest(
        token,
        textMessageWithName,
        phoneNumber,
        mediaUrl,
        sendMessageUrl,
        user?.id,
        activeChat?.id,
        activeChat?.setor_id,
      );

      if (response?.error) {
        setIsMessageSendingError(true);

        switch (response?.message) {
          case 'Mensagem nao enviada' || !response:
            setErrorMessage(
              'Não foi possível enviar mensagem! Confira o status da sua conexão WhatsApp em https://interface.telein.com.br/  e tente novamente.\n\n Caso o erro persista, entre em contato com o suporte.',
            );
            break;
          case 'Error: no account exists':
            setErrorMessage(
              'Remova ou inclua o "9" a frente do telefone. Caso persista, verifique se o destinatário realmente possui WhatsApp.',
            );
            break;
          case 'Essa mensagem foi enviada fora do período permitido.':
            setErrorMessage(
              'Não foi possível enviar mensagem: Fora do período permitido',
            );
            break;
          default:
            setErrorMessage(
              'Ocorreu um erro inesperado ao enviar mensagem. Entre em contato com o suporte.',
            );
        }

        return response;
      }

      if (response?.mediaUrl) {
        responseMediaUrl = await getRequest(
          `${baseMediaUrl}${response.mediaUrl}`,
        );
      }

      const sentMessageId = response?.data?.key?.id
        || response?.message_id
        || response?.messages?.[0]?.id;

      if (sentMessageId) {
        setMessages((prevMessages) => updateOptimisticMessageDeliveryStatus(
          prevMessages,
          clientMessageId,
          {
            idMsg: sentMessageId,
            mediaUrl: response.mediaUrl ? responseMediaUrl.url : undefined,
            ack: 1,
          },
        ));

        setQuickAnswers((prevQuickAnswers) => ({
          ...prevQuickAnswers,
          messagesWithMediaUrl: updateOptimisticMessageDeliveryStatus(
            prevQuickAnswers.messagesWithMediaUrl,
            clientMessageId,
            {
              idMsg: sentMessageId,
              mediaUrl: response.mediaUrl ? responseMediaUrl.url : undefined,
            },
          ),
        }));
      }

      return response;
    } catch (error) {
      setErrorMessage(
        'Ocorreu um erro inesperado ao enviar mensagem. Entre em contato com o suporte.',
      );
      return undefined;
    } finally {
      setIsMessagesLoading(false);
    }
  }, [
    audioBlobURL,
    currentChat,
    handleScrollChatToBottom,
    openTickets,
    setErrorMessage,
    setIsMessageSendingError,
    setIsMessagesLoading,
    setMessages,
    setOpenTickets,
    setQuickAnswers,
    setShowNewChat,
    setSortedChats,
    socket,
    sortedChats,
    urlDocumentToSend,
    urlImageToSend,
    urlVideoToSend,
    user,
  ]);

  const handleSendNewChatTemplateMessage = useCallback(async ({
    chat,
    template,
  }) => {
    let clientMessageId = null;

    try {
      if (!chat || !template) {
        return undefined;
      }

      setIsMessagesLoading(true);

      const { sessionId } = user.senders[chat.sender];
      const payload = {
        token: sessionId,
        template_name: template.name,
        telefone: chat.fromMsg,
        language: template.language,
      };

      clientMessageId = [
        'template',
        chat.sender,
        chat.fromMsg,
        Date.now(),
        Math.random().toString(36).slice(2),
      ].join('-');

      const message = {
        body: template?.components,
        clientMessageId,
        fromMe: 1,
        idMsg: null,
        isDeleted: null,
        sender: chat.sender,
        fromMsg: chat.fromMsg,
        createdAt: Date.now(),
        mediaType: 'template',
        mediaUrl: '',
        api: user.senders[chat.sender]?.api_tipo === 1 ? 1 : chat.api,
        ack: 0,
        read: 0,
        isTemplateSending: true,
        isTemplateSendError: false,
      };

      await fetchMessages(chat, 1);

      if (!isRequestStillActive(buildChatRequestKey(chat))) {
        return undefined;
      }

      appendMessageIfMissing(message);
      setIsMessagesLoading(true);
      setOfficialApiTemplates((prevTemplates) => ({
        ...prevTemplates,
        open: false,
        loading: false,
        selectedToSend: null,
        isOfficialApiTemplateSended: true,
      }));
      setIsStartingNewChatTemplate(false);

      handleScrollChatToBottom();

      const response = await postRequest(`${sendTemplateMessageUrl}`, payload);

      if (response?.error || !response) {
        setMessages((prevMessages) => updateOptimisticMessageDeliveryStatus(
          prevMessages,
          clientMessageId,
          {
            isTemplateSending: false,
            isTemplateSendError: true,
          },
        ));

        switch (response?.message) {
          case 'Mensagem nao enviada':
            setErrorMessage(
              'NÃ£o foi possÃ­vel enviar template! Confira o status da sua conexÃ£o WhatsApp em https://interface.telein.com.br/  e tente novamente.\n\n Caso o erro persista, entre em contato com o suporte.',
            );
            break;
          case 'Error: no account exists':
            setErrorMessage(
              'Remova ou inclua o "9" a frente do telefone. Caso persista, verifique se o destinatÃ¡rio realmente possui WhatsApp.',
            );
            break;
          case 'Essa mensagem foi enviada fora do perÃ­odo permitido.':
            setErrorMessage(
              'NÃ£o foi possÃ­vel enviar mensagem: Fora do perÃ­odo permitido',
            );
            break;
          default:
            setErrorMessage(
              'Ocorreu um erro inesperado ao enviar mensagem. Entre em contato com o suporte.',
            );
        }

        return response;
      }

      const sentTemplateMessageId = response?.messages?.[0]?.id;
      const messageWithId = {
        ...message,
        idMsg: sentTemplateMessageId,
        ack: sentTemplateMessageId ? 1 : 0,
        isTemplateSending: false,
        isTemplateSendError: false,
      };

      setSortedChats((prevSortedChats) => updateUserChatsWithNewMessage(
        prevSortedChats,
        messageWithId,
        chat,
      ));
      setOpenTickets((prevOpenTickets) => updateUserChatsWithNewMessage(
        prevOpenTickets,
        messageWithId,
        chat,
      ));
      setShowNewChat(false);

      socket.emit(
        'sendedMessage',
        chat,
        messageWithId,
        user.ambiente,
        user.id,
      );

      setMessages((prevMessages) => updateOptimisticMessageDeliveryStatus(
        prevMessages,
        clientMessageId,
        {
          idMsg: sentTemplateMessageId,
          ack: sentTemplateMessageId ? 1 : 0,
          isTemplateSending: false,
          isTemplateSendError: false,
        },
      ));
      setTimeout(handleScrollChatToBottom, 500);

      return response;
    } catch (error) {
      if (clientMessageId) {
        setMessages((prevMessages) => updateOptimisticMessageDeliveryStatus(
          prevMessages,
          clientMessageId,
          {
            isTemplateSending: false,
            isTemplateSendError: true,
          },
        ));
      }

      setErrorMessage('NÃ£o foi possÃ­vel enviar mensagem template. Entre em contato com o suporte');
      return undefined;
    } finally {
      setIsStartingNewChatTemplate(false);
      setIsMessagesLoading(false);
    }
  }, [
    appendMessageIfMissing,
    buildChatRequestKey,
    fetchMessages,
    handleScrollChatToBottom,
    isRequestStillActive,
    setErrorMessage,
    setIsMessagesLoading,
    setIsStartingNewChatTemplate,
    setMessages,
    setOfficialApiTemplates,
    setOpenTickets,
    setShowNewChat,
    setSortedChats,
    socket,
    user,
  ]);

  const handleSendTemplateMessage = useCallback(async () => {
    try {
      setIsMessagesLoading(true);
      let activeChat = currentChat;

      if (!isAComment && !currentChat.is_internal_chat) {
        const promotedChat = await updateUserChatFromQueueToChats(currentChat);

        if (!promotedChat) {
          return;
        }

        activeChat = promotedChat;
      }

      const { sessionId } = user.senders[activeChat.sender];

      const payload = {
        token: sessionId,
        template_name: officialApiTemplates.selectedToSend.name,
        telefone: activeChat.fromMsg,
        language: officialApiTemplates.selectedToSend.language,
      };

      const clientMessageId = [
        'template',
        activeChat.sender,
        activeChat.fromMsg,
        Date.now(),
        Math.random().toString(36).slice(2),
      ].join('-');

      const message = {
        body: officialApiTemplates?.selectedToSend?.components,
        clientMessageId,
        fromMe: 1,
        idMsg: null,
        isDeleted: null,
        sender: activeChat.sender,
        fromMsg: activeChat.fromMsg,
        createdAt: Date.now(),
        mediaType: 'template',
        mediaUrl: '',
        api: activeChat.api,
        read: 0,
      };

      setMessages((prevMessages) => [message, ...prevMessages]);
      setIsMessagesLoading(false);
      setOfficialApiTemplates((prevTemplates) => ({
        ...prevTemplates,
        open: false,
        loading: false,
        selectedToSend: null,
        isOfficialApiTemplateSended: true,
      }));

      handleScrollChatToBottom();

      const response = await postRequest(`${sendTemplateMessageUrl}`, payload);

      if (response?.error) {
        switch (response?.message) {
          case 'Mensagem nao enviada' || !response:
            setErrorMessage(
              'Não foi possível enviar template! Confira o status da sua conexão WhatsApp em https://interface.telein.com.br/  e tente novamente.\n\n Caso o erro persista, entre em contato com o suporte.',
            );
            break;
          case 'Error: no account exists':
            setErrorMessage(
              'Remova ou inclua o "9" a frente do telefone. Caso persista, verifique se o destinatário realmente possui WhatsApp.',
            );
            break;
          case 'Essa mensagem foi enviada fora do período permitido.':
            setErrorMessage(
              'Não foi possível enviar mensagem: Fora do período permitido',
            );
            break;
          default:
            setErrorMessage(
              'Ocorreu um erro inesperado ao enviar mensagem. Entre em contato com o suporte.',
            );
        }

        return;
      }

      const updatedSortedChats = updateUserChatsWithNewMessage(
        sortedChats,
        message,
      );

      setSortedChats(updatedSortedChats);
      setShowNewChat(false);

      const sentTemplateMessageId = response?.messages?.[0]?.id;

      const messageWithId = {
        ...message,
        idMsg: sentTemplateMessageId,
      };

      socket.emit(
        'sendedMessage',
        activeChat,
        messageWithId,
        user.ambiente,
        user.id,
      );

      setTimeout(handleScrollChatToBottom, 500);

      if (sentTemplateMessageId) {
        setMessages((prevMessages) => updateOptimisticMessageDeliveryStatus(
          prevMessages,
          clientMessageId,
          {
            idMsg: sentTemplateMessageId,
          },
        ));
      }
    } catch (error) {
      setErrorMessage('Não foi possível enviar mensagem template. Entre em contato com o suporte');
    } finally {
      setIsMessagesLoading(false);
    }
  }, [
    currentChat,
    handleScrollChatToBottom,
    isAComment,
    officialApiTemplates,
    setErrorMessage,
    setIsMessagesLoading,
    setMessages,
    setOfficialApiTemplates,
    setShowNewChat,
    setSortedChats,
    socket,
    sortedChats,
    updateUserChatFromQueueToChats,
    user,
  ]);

  const handleShowAudioTranscription = useCallback(async (mediaUrl, idMsg, body) => {
    const currentAudioTranscription = useMessageStore.getState().audioTranscription;
    const transcriptionExists = currentAudioTranscription.transcriptionText.find(
      (transcription) => transcription.idMsg === idMsg,
    );

    if (transcriptionExists) {
      setAudioTranscription((prevAudioTranscription) => ({
        ...prevAudioTranscription,
        transcriptionText: prevAudioTranscription.transcriptionText.map(
          (transcription) => (transcription.idMsg === idMsg
            ? {
              ...transcription,
              showAudioTranscription: !transcription.showAudioTranscription,
            }
            : transcription),
        ),
      }));
      return;
    }

    if (body !== '') {
      setAudioTranscription((prevAudioTranscription) => ({
        ...prevAudioTranscription,
        transcriptionText: [
          ...prevAudioTranscription.transcriptionText,
          { idMsg, body, showAudioTranscription: true },
        ],
      }));
      return;
    }

    setAudioTranscription((prevAudioTranscription) => ({
      ...prevAudioTranscription,
      isLoadingTranscription: true,
    }));

    const formData = new FormData();
    formData.append('url', mediaUrl);

    try {
      const response = await postFormDataRequest(
        `${webhookTeleinUrl}/ia/audiototext_url.php`,
        formData,
      );

      const payload = {
        idMsg,
        body: response.text,
        showAudioTranscription: true,
      };

      await patchRequest(
        `${baseUrl}/messages/updateBodyById`,
        { idMsg, body: response.text },
      );

      setAudioTranscription((prevAudioTranscription) => ({
        ...prevAudioTranscription,
        isLoadingTranscription: false,
        transcriptionText: [
          ...prevAudioTranscription.transcriptionText,
          payload,
        ],
      }));
    } catch (error) {
      setErrorMessage(
        'Não foi possível transcrever áudio! Entre em contato com o suporte',
      );

      setAudioTranscription((prevAudioTranscription) => ({
        ...prevAudioTranscription,
        isLoadingTranscription: false,
      }));
    }
  }, [setAudioTranscription, setErrorMessage]);

  return {
    fetchMediaUrls,
    getMessages,
    fetchMessages,
    fetchInternalMessages,
    sendMessage,
    handleSendNewChatTemplateMessage,
    handleSendTemplateMessage,
    handleShowAudioTranscription,
  };
}
